// V4App.tsx — the Alkio Mobile v4 mobile shell (milestone M-PWA-OPERATOR-UX-V2,
// slice 1). A self-contained mobile experience — since the default cutover it
// is the landing surface (bare URL / #v4); the legacy shell lives at #legacy.
//
// Shell backbone (per IMPLEMENTATION-GUIDE.md):
//   • Providers: <TimeShiftProvider> wraps the app (Pattern 2 backbone).
//   • 6 tabs: Home, Activity, Roadmap, Entities, Dispatch, Docs + bottom tab-bar.
//   • Global bridges: window.__openEntity / __goTab / __openScrubber /
//     __openSystemHealth.
//   • Active screen re-renders keyed by asOf (live vs. scrubbed).
//   • Root-mounted <EntitySheet> = universal drill-through (Pattern 1).
//
// Slice 4 (MUST-INTEGRATE legacy features): the ScreenHeader is ambient —
// calm/attention/alert derived from the dashboard attentionFeed exactly like
// the legacy UnifiedHeader, with the kill-switch surfacing in alert state —
// plus a gear opening the Settings overlay (the guide's TweaksPanel slot) and
// a root-mounted System Health drill-through sheet.

import { useEffect, useState } from "react";
import { TimeShiftProvider, useTimeShift } from "./TimeShift.js";
import { TabBar, type V4Tab } from "./TabBar.js";
import { EntitySheet } from "./EntitySheet.js";
import { DocsSheet } from "./DocsSheet.js";
import { TimeScrubber } from "./TimeScrubber.js";
import { pushEntity, restoreFromUrl } from "./entityStore.js";
import { normalizeType, pathTypeFromId } from "./entityTypes.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { Icons } from "../components/Icons.js";
import { useAmbient } from "./ambient.js";
import { fetchDigitalThreadHealth } from "../api/client.js";
import type { DigitalThreadHealthResponse } from "../api/types.js";
import { SystemHealthSheet } from "./SystemHealth.js";
import { VentureSwitcher } from "./VentureSwitcher.js";
import { HomeScreen } from "./screens/Home.js";
import { ActivityScreen } from "./screens/Activity.js";
import { EntitiesScreen } from "./screens/Entities.js";
import { RoadmapScreen } from "./screens/Roadmap.js";
import { DispatchScreen } from "./screens/Dispatch.js";
import { DocsScreen } from "./screens/Docs.js";
import { SettingsOverlay } from "./screens/Settings.js";

const AMBIENT_TONE = {
  calm: { bg: "var(--bg-bar)", stroke: "var(--border-soft)", accent: "var(--ok)" },
  attention: { bg: "var(--warn-soft)", stroke: "var(--warn)", accent: "var(--warn)" },
  alert: { bg: "var(--danger-soft)", stroke: "var(--danger)", accent: "var(--danger)" },
} as const;

function ScreenHeader({
  tab,
  onOpenSettings,
}: {
  tab: V4Tab;
  onOpenSettings: () => void;
}) {
  const { asOf, isLive } = useTimeShift();
  const { state, pulse, killable, killed, toggleKilled } = useAmbient();
  const [threadHealth, setThreadHealth] = useState<DigitalThreadHealthResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchDigitalThreadHealth().then((t) => {
      if (!cancelled) setThreadHealth(t);
    }).catch(() => {/* non-fatal */});
    return () => { cancelled = true; };
  }, []);
  const tone = AMBIENT_TONE[state];
  const titles: Record<V4Tab, string> = {
    // "home" carries no title text of its own — the VentureSwitcher chip
    // (venture name) sits where "Alkio" used to sit statically.
    home: "",
    activity: "Activity",
    roadmap: "Roadmap",
    entities: "Entities",
    dispatch: "Dispatch",
    docs: "Docs",
  };
  const asOfLabel =
    !isLive && asOf
      ? new Date(asOf).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "14px 12px 12px 20px",
        borderBottom: `1px solid ${tone.stroke}`,
        background: tone.bg,
        transition: "background .25s var(--ease)",
      }}
    >
      <VentureSwitcher />

      {titles[tab] && (
        <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: 0.2, flexShrink: 0 }}>
          {titles[tab]}
        </span>
      )}

      {/* Ambient pulse — legacy UnifiedHeader derivation (calm/attention/alert) */}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "0 2px",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: tone.accent,
            boxShadow: state !== "calm" ? `0 0 0 2px ${"var(--bg)"}` : "none",
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            fontWeight: state === "calm" ? 400 : 700,
            color: state === "calm" ? "var(--text-dim)" : tone.accent,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {pulse}
        </span>
      </span>

      {/* Digital thread + embedding coverage chips (SC-4C) */}
      {threadHealth != null && (
        <>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: threadHealth.annotationCoverage >= 85 ? "var(--ok)" : threadHealth.annotationCoverage >= 70 ? "var(--warn)" : "var(--danger)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            title="Digital thread annotation coverage"
          >
            thread {threadHealth.annotationCoverage}%
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: (threadHealth.embeddingCoverage ?? 0) >= 85 ? "var(--ok)" : (threadHealth.embeddingCoverage ?? 0) >= 70 ? "var(--warn)" : "var(--text-dim)",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            title="Embedding coverage"
          >
            emb {threadHealth.embeddingCoverage ?? 0}%
          </span>
        </>
      )}

      {/* Kill-switch — surfaces only in alert state, like the legacy header */}
      {killable && (
        <button
          onClick={toggleKilled}
          style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.06em",
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 4,
            background: killed ? "var(--bg)" : "var(--danger)",
            color: killed ? "var(--danger)" : "var(--bg)",
            border: killed ? "1px solid var(--danger)" : "1px solid transparent",
            whiteSpace: "nowrap",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          {killed ? "RESUME" : "PAUSE"}
        </button>
      )}

      {/* Time chip — pure scrubber affordance (ambient state lives in the pulse) */}
      <button
        className={`chip ${isLive ? "" : "warn"}`.trim()}
        style={{ fontSize: 10, letterSpacing: 0.5, cursor: "pointer", flexShrink: 0 }}
        title="Open the timeline scrubber"
        onClick={() => window.__openScrubber?.()}
      >
        {isLive ? "◷ LIVE" : `◷ ${asOfLabel}`}
      </button>

      {/* Settings gear → the slice-4 Settings overlay (TweaksPanel slot) */}
      <button
        aria-label="Settings"
        onClick={onOpenSettings}
        style={{
          minWidth: 36,
          minHeight: 36,
          borderRadius: 6,
          background: "transparent",
          border: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <Icons.Settings size={15} color="var(--text-muted)" />
      </button>
    </header>
  );
}

function ActiveScreen({ tab }: { tab: V4Tab }) {
  switch (tab) {
    case "home":
      return <HomeScreen />;
    case "activity":
      return <ActivityScreen />;
    case "entities":
      return <EntitiesScreen />;
    case "roadmap":
      return <RoadmapScreen />;
    case "dispatch":
      return <DispatchScreen />;
    case "docs":
      return <DocsScreen />;
  }
}

function Inner() {
  const [tab, setTab] = useState<V4Tab>(() => window.location.hash === "#docs" ? "docs" : "home");
  const [scrubberOpen, setScrubberOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const { asOf } = useTimeShift();

  // Global bridges — set once on mount so linkPills anywhere can drive navigation.
  useEffect(() => {
    window.__openEntity = (type: string, id: string) => {
      const norm = normalizeType(type) ?? pathTypeFromId(id);
      if (norm) pushEntity(norm, id);
    };
    window.__goTab = (t: string) => {
      const valid: V4Tab[] = ["home", "activity", "roadmap", "entities", "dispatch", "docs"];
      if ((valid as string[]).includes(t)) setTab(t as V4Tab);
    };
    window.__openScrubber = () => setScrubberOpen(true);
    window.__openSystemHealth = () => setHealthOpen(true);
    restoreFromUrl();
    return () => {
      delete window.__openEntity;
      delete window.__goTab;
      delete window.__openScrubber;
      delete window.__openSystemHealth;
    };
  }, []);

  return (
    <div className="phone">
      <ScreenHeader tab={tab} onOpenSettings={() => setSettingsOpen(true)} />
      <div className="scroll">
        {/* Keyed by asOf so scrubbing re-renders the active screen "as of" that time. */}
        <ErrorBoundary name={`v4:${tab}`} key={`${tab}:${asOf ?? "live"}`}>
          <ActiveScreen tab={tab} />
        </ErrorBoundary>
      </div>
      <TabBar active={tab} onChange={setTab} />
      <EntitySheet />
      <DocsSheet />
      {scrubberOpen && <TimeScrubber onClose={() => setScrubberOpen(false)} />}
      {healthOpen && (
        <ErrorBoundary name="v4:system-health">
          <SystemHealthSheet onClose={() => setHealthOpen(false)} />
        </ErrorBoundary>
      )}
      {settingsOpen && (
        <ErrorBoundary name="v4:settings">
          <SettingsOverlay onClose={() => setSettingsOpen(false)} />
        </ErrorBoundary>
      )}
    </div>
  );
}

export function V4App() {
  return (
    <TimeShiftProvider>
      <Inner />
    </TimeShiftProvider>
  );
}
